export type CacheEntry<T> = {
  value: T;
  timestamp: number;
  expiresAt: number;
};

export class CacheManager {
  static readonly ttl = {
    exercises: 30 * 60 * 1000,
    muscleGroups: 60 * 60 * 1000,
    feedbackPending: 60 * 60 * 1000,
    workoutHistory: 2 * 60 * 60 * 1000,
    maxWeight: 2 * 60 * 60 * 1000,
    progressAnalytics: 15 * 60 * 1000,
    progressAnalyticsEmpty: 5 * 60 * 1000,
  } as const;

  static isCacheEnabled(): boolean {
    const override = process.env.REACT_APP_CACHE_ENABLED;
    if (override === 'true') return true;
    if (override === 'false') return false;
    return process.env.NODE_ENV !== 'development';
  }

  static readonly storage = {
    getItem(key: string): string | null {
      if (!CacheManager.isCacheEnabled()) return null;
      try {
        if (typeof window === 'undefined' || !('localStorage' in window)) {
          return null;
        }
        return window.localStorage.getItem(key);
      } catch {
        return null;
      }
    },

    setItem(key: string, value: string): void {
      if (!CacheManager.isCacheEnabled()) return;
      try {
        if (typeof window === 'undefined' || !('localStorage' in window)) {
          return;
        }
        window.localStorage.setItem(key, value);
      } catch {
        // ignore
      }
    },

    removeItem(key: string): void {
      if (!CacheManager.isCacheEnabled()) return;
      try {
        if (typeof window === 'undefined' || !('localStorage' in window)) {
          return;
        }
        window.localStorage.removeItem(key);
      } catch {
        // ignore
      }
    },

    getJson<T>(key: string): T | null {
      const raw = CacheManager.storage.getItem(key);
      if (!raw) return null;
      try {
        return JSON.parse(raw) as T;
      } catch {
        return null;
      }
    },

    setJson(key: string, value: unknown): void {
      try {
        CacheManager.storage.setItem(key, JSON.stringify(value));
      } catch {
        // ignore
      }
    },
  };

  static get<K, T>(map: Map<K, CacheEntry<T>>, key: K): T | null {
    if (!CacheManager.isCacheEnabled()) {
      return null;
    }

    const entry = map.get(key);
    if (!entry) {
      return null;
    }

    if (entry.expiresAt <= Date.now()) {
      map.delete(key);
      return null;
    }

    return entry.value;
  }

  static set<K, T>(map: Map<K, CacheEntry<T>>, key: K, value: T, ttlMs: number): void {
    if (!CacheManager.isCacheEnabled()) {
      return;
    }

    const now = Date.now();
    map.set(key, {
      value,
      timestamp: now,
      expiresAt: now + ttlMs,
    });
  }

  static clear<K, T>(map: Map<K, CacheEntry<T>>): void {
    map.clear();
  }

  static delete<K, T>(map: Map<K, CacheEntry<T>>, key: K): void {
    map.delete(key);
  }
}
